import React, { useState } from "react";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { blogPosts } from "../data/blogData";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  const filtered = searchTerm.trim()
    ? blogPosts.filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;

  const showSearch = !!filtered;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      {/* Header / Search */}
      <div
        className="bg-[#012d5c] pt-32 pb-16 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">Integro News & Education</h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Latest news, medical research, and patient guides from our clinical team.
          </p>
          <div className="max-w-xl mx-auto relative">
            <Input
              type="text"
              placeholder="Search articles, conditions, or topics..."
              className="w-full pl-12 py-6 rounded-lg text-black bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">

        {showSearch ? (
          <>
            <h2 className="text-2xl font-bold text-[#012d5c] mb-6">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""} for &ldquo;{searchTerm}&rdquo;
            </h2>
            {filtered.length === 0 ? (
              <p className="text-gray-500">No articles matched your search. Try a different term.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Featured */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-[#012d5c] mb-6 flex items-center">
                <span className="w-2 h-8 bg-[#4caf50] mr-3 rounded-full" />
                Featured Article
              </h2>
              <Link
                to={`/blog/${featured.slug}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col lg:flex-row hover:shadow-xl transition-shadow group"
              >
                <div className="lg:w-1/2 overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover min-h-[300px] transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-[#e0f2fe] text-[#005eb8] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                      {featured.category}
                    </span>
                    <span className="text-gray-500 text-sm">{featured.date}</span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#012d5c] mb-4 group-hover:text-[#005eb8] transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">{featured.excerpt}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-sm font-medium text-gray-800">By {featured.author}</span>
                    <span className="text-[#4caf50] font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                      Read More →
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Grid */}
            <h2 className="text-2xl font-bold text-[#012d5c] mb-6">Latest Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((post) => (
                <PostCard key={post.slug} post={post} />
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

const PostCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group border border-gray-100 flex flex-col"
  >
    <div className="h-48 overflow-hidden">
      <img
        src={post.image}
        alt={post.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[#4caf50] text-xs font-bold uppercase tracking-wider">
          {post.category}
        </span>
        <span className="text-gray-400 text-xs">•</span>
        <span className="text-gray-400 text-xs">{post.date}</span>
      </div>
      <h3 className="text-xl font-bold text-[#012d5c] mb-3 line-clamp-2 group-hover:text-[#005eb8] transition-colors">
        {post.title}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">
        {post.excerpt}
      </p>
      <span className="text-[#005eb8] text-sm font-medium group-hover:underline">
        Read Article →
      </span>
    </div>
  </Link>
);

export default Blog;
